import { 
  avitoRepository, 
  AvitoSettings,
  UpdateAvitoSettingsRequest,
  AvitoTemplate,
  CreateAvitoTemplateRequest,
  UpdateAvitoTemplateRequest,
  AvitoСategoryMapping,
  CreateAvitoMappingRequest,
  UpdateAvitoMappingRequest,
  AvitoCategory,
  GenerateXMLResponse,
  AvitoHealthCheck,
  AvitoStatistics, 
  AvitoAd 
} from '@/infrastructure/repositories';
import { HttpError } from '@/domain/entities';

export class AvitoService {
  // Settings
  async getSettings(): Promise<AvitoSettings> {
    try {
      return await avitoRepository.getSettings();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito settings');
    }
  }

  async updateSettings(data: UpdateAvitoSettingsRequest): Promise<AvitoSettings> {
    try {
      return await avitoRepository.updateSettings(data);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to update Avito settings');
    }
  }

  async regenerateXMLToken(): Promise<{ token: string; url: string }> {
    try {
      return await avitoRepository.regenerateXMLToken();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to regenerate XML token');
    }
  }

  // Templates
  async getAllTemplates(): Promise<AvitoTemplate[]> {
    try {
      return await avitoRepository.getAllTemplates();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito templates');
    }
  }

  async getTemplate(key: string): Promise<AvitoTemplate> {
    try {
      return await avitoRepository.getTemplate(key);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito template');
    }
  }

  async createTemplate(data: CreateAvitoTemplateRequest): Promise<AvitoTemplate> {
    try {
      return await avitoRepository.createTemplate(data);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to create Avito template');
    }
  }

  async updateTemplate(key: string, data: UpdateAvitoTemplateRequest): Promise<AvitoTemplate> {
    try {
      return await avitoRepository.updateTemplate(key, data);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to update Avito template');
    }
  }

  async deleteTemplate(key: string): Promise<void> {
    try {
      return await avitoRepository.deleteTemplate(key);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to delete Avito template');
    }
  }

  // Category Mappings
  async getAllMappings(): Promise<AvitoСategoryMapping[]> {
    try {
      return await avitoRepository.getAllMappings();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito mappings');
    }
  }

  async getMapping(id: number): Promise<AvitoСategoryMapping> {
    try {
      return await avitoRepository.getMapping(id);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito mapping');
    }
  }

  async createMapping(data: CreateAvitoMappingRequest): Promise<AvitoСategoryMapping> {
    try {
      return await avitoRepository.createMapping(data);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to create Avito mapping');
    }
  }

  async updateMapping(id: number, data: UpdateAvitoMappingRequest): Promise<AvitoСategoryMapping> {
    try {
      return await avitoRepository.updateMapping(id, data);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to update Avito mapping');
    }
  }

  async deleteMapping(id: number): Promise<void> {
    try {
      return await avitoRepository.deleteMapping(id);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to delete Avito mapping');
    }
  }

  // Categories
  async getSupportedCategories(): Promise<AvitoCategory[]> {
    try {
      return await avitoRepository.getSupportedCategories();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch supported categories');
    }
  }

  // XML Generation
  async generateXML(): Promise<GenerateXMLResponse> {
    try {
      return await avitoRepository.generateXML();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to generate XML');
    }
  }

  // Health Check
  async healthCheck(): Promise<AvitoHealthCheck> {
    try {
      return await avitoRepository.healthCheck();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to check Avito health');
    }
  }

  // Legacy API
  async getStatistics(): Promise<AvitoStatistics> {
    try {
      return await avitoRepository.getStatistics();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito statistics');
    }
  }

  async getAds(): Promise<{ ads: AvitoAd[]; total: number }> {
    try {
      return await avitoRepository.getAds();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito ads');
    }
  }

  async getStatus(): Promise<{ connected: boolean; lastSync?: string }> {
    try {
      return await avitoRepository.getStatus();
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch Avito status');
    }
  }
}

export const avitoService = new AvitoService();

